import React, { useState, useEffect, useCallback } from "react";
import {
  PlusCircle,
  Trash2,
  Edit2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  X,
  Image as ImageIcon,
  Calendar,
  User,
  Tag,
} from "lucide-react";
import marathiService from "../../services/marathiService";

const Alert = ({ children, variant, className = "" }) => {
  const baseStyles = "flex w-full rounded-lg border p-4";
  const variantStyles =
    variant === "destructive"
      ? "border-red-200 bg-red-50 text-red-600"
      : "border-gray-200 bg-gray-50 text-gray-600";

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = "" }) => (
  <div className={`text-sm ${className}`}>{children}</div>
);

const MarathiQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);

  const [formData, setFormData] = useState({
    content: "",
    image: "",
    author: "",
    categories: [],
    date: new Date().toISOString().split("T")[0],
  });

  const [newCategory, setNewCategory] = useState("");

  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await marathiService.quotes.getAll({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
      });

      console.log("Fetched quotes response:", response); // Debug log

      if (Array.isArray(response)) {
        setQuotes(response);
        setTotalPages(Math.ceil(response.length / itemsPerPage) || 1);
      } else if (response && response.data) {
        setQuotes(response.data);
        setTotalPages(
          response.totalPages ||
            Math.ceil(response.data.length / itemsPerPage) ||
            1
        );
      } else {
        console.error("Unexpected response format:", response);
        setQuotes([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error fetching quotes:", err);
      setError(err.message || "Failed to fetch quotes");
      setQuotes([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await marathiService.quotes.delete(selectedQuote._id);
      setSuccess("Quote deleted successfully!");
      setShowDeleteModal(false);
      fetchQuotes();
    } catch (err) {
      console.error("Error deleting quote:", err);
      setError(err.message || "Failed to delete quote");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.content.trim()) {
      setError("Quote content is required");
      return;
    }

    if (!formData.author.trim()) {
      setError("Author is required");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      // Prepare the data object
      const submissionData = {
        content: formData.content,
        author: formData.author,
        date: formData.date,
        categories: formData.categories,
        image: formData.image || null, // Ensure image is null if empty
      };

      // Make API call
      let response;
      if (selectedQuote) {
        response = await marathiService.quotes.update(selectedQuote._id, submissionData);
      } else {
        response = await marathiService.quotes.create(submissionData);
      }

      // Check for errors
      if (!response.success && !response.data) {
        throw new Error(response.message || "Operation failed");
      }

      // Handle success
      setSuccess(
        selectedQuote
          ? "Quote updated successfully!"
          : "Quote created successfully!"
      );
      await fetchQuotes();
      setShowFormModal(false);
    } catch (error) {
      console.error("Error saving quote:", error);
      setError(
        error.message ||
          `Failed to ${selectedQuote ? "update" : "create"} quote`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size should be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          image: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addCategory = () => {
    if (
      newCategory.trim() &&
      !formData.categories.includes(newCategory.trim())
    ) {
      setFormData({
        ...formData,
        categories: [...formData.categories, newCategory.trim()],
      });
      setNewCategory("");
    }
  };

  const removeCategory = (categoryToRemove) => {
    setFormData({
      ...formData,
      categories: formData.categories.filter((cat) => cat !== categoryToRemove),
    });
  };

  const openEditModal = (quote) => {
    setSelectedQuote(quote);
    setFormData({
      content: quote.content || "",
      image: quote.image || "",
      author: quote.author || "",
      categories: quote.categories || [],
      date: quote.date
        ? new Date(quote.date).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
    });
    setShowFormModal(true);
  };

  const openCreateModal = () => {
    setSelectedQuote(null);
    setFormData({
      content: "",
      image: "",
      author: "",
      categories: [],
      date: new Date().toISOString().split("T")[0],
    });
    setShowFormModal(true);
  };

  const getImageUrl = (image) => {
    if (!image) return '/placeholder.jpg';
    
    // If it's a base64 data URL, return it as is
    if (typeof image === 'string' && image.startsWith('data:image/')) {
      return image;
    }
    
    // If it's a file object, create an object URL
    if (image instanceof File || image instanceof Blob) {
      return URL.createObjectURL(image);
    }
    
    // If it's a string but not a data URL (assuming it's a path)
    if (typeof image === 'string') {
      // If it's already a full URL, return it as is
      if (image.startsWith('http')) return image;
      
      // For file paths, generate the correct URL
      const path = image.replace(/^api\//, '');
      const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5500';
      return `${baseUrl}/${path}`;
    }
    
    // Fallback to placeholder
    return '/placeholder.jpg';
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Marathi Quotes Management
        </h1>
        <button
          onClick={openCreateModal}
          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <PlusCircle className="mr-2" size={20} />
          Add New Quote
        </button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="animate-spin text-red-600" size={32} />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quotes.length > 0 ? (
              quotes.map((quote) => (
                <div
                  key={quote._id}
                  className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow"
                >
                  {quote.image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={getImageUrl(quote.image)}
                        alt="Quote background"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/placeholder.jpg';
                        }}
                      />
                    </div>
                  )}

                  <div className="p-5 bg-white">
                    <p className="text-lg italic text-gray-800 mb-4">
                      "{quote.content}"
                    </p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-gray-600">
                        <User size={16} className="mr-2" />
                        {quote.author}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Calendar size={16} className="mr-2" />
                        {new Date(quote.date).toLocaleDateString()}
                      </div>
                      {quote.categories?.length > 0 && (
                        <div className="flex items-start text-gray-600">
                          <Tag size={16} className="mr-2 mt-1" />
                          <div className="flex flex-wrap gap-1">
                            {quote.categories.map((cat, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                              >
                                {cat}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(quote)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <Edit2 size={20} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedQuote(quote);
                          setShowDeleteModal(true);
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-600">
                  No quotes found. Create one to get started!
                </p>
              </div>
            )}
          </div>

          {quotes.length > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {quotes.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, quotes.length)}
                    </span>{" "}
                    of <span className="font-medium">{quotes.length}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => goToPage(1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span className="sr-only">First</span>
                      <ChevronsLeft size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? "z-10 bg-red-50 border-red-500 text-red-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span className="sr-only">Last</span>
                      <ChevronsRight size={16} />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Quote</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this quote? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal - Updated to match article styling */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {selectedQuote ? 'Edit Quote' : 'Add Quote'}
                </h2>
                <button
                  onClick={() => setShowFormModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quote Content *
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[100px]"
                    required
                  />
                </div>

                {/* Author */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author *
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    required
                  />
                </div>

                {/* Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image (Optional)
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.image && (
                      <img
                        src={typeof formData.image === 'string' 
                          ? getImageUrl(formData.image) 
                          : URL.createObjectURL(formData.image)}
                        alt="Preview"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <label className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-500">
                        <ImageIcon className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Upload image</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Categories */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categories
                  </label>
                  <div className="flex mb-2">
                    <input
                      type="text"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      className="flex-1 p-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Add a category"
                    />
                    <button
                      type="button"
                      onClick={addCategory}
                      className="px-4 py-2 bg-red-600 text-white rounded-r-lg hover:bg-red-700"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.categories.map((cat, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm"
                      >
                        {cat}
                        <button
                          type="button"
                          onClick={() => removeCategory(cat)}
                          className="ml-1.5 inline-flex text-red-600 hover:text-red-900"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (selectedQuote ? 'Update Quote' : 'Create Quote')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarathiQuotes;